import {
  ChangeDetectionStrategy, Component, ElementRef, HostListener,
  inject, signal,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { AppService, Lang } from '../../../core/app.service';
import { APP_LANGUAGES, AppLanguage, LANG_FLAGS } from '../../../core/languages';
import { IconComponent } from '../../icon/icon.component';

/** The rich menu renders one {@link AppLanguage} per row — see `core/languages.ts` for the list. */
type LangOption = AppLanguage;

const LANGS: readonly LangOption[] = APP_LANGUAGES;

/**
 * Variant-A language switcher — a globe + the current language code in
 * the topbar, sitting between the role toggle and the theme button.
 * Click opens a rich menu listing each language in its own script, with
 * its English name, region, and an RTL tag where it matters.
 *
 * <p>Selecting Persian or Arabic mirrors the whole interface to RTL
 * because {@link AppService#setLang} writes {@code dir="rtl"} onto
 * {@code <html>}. The strings themselves are not (yet) translated —
 * that's a separate catalogue step.
 */
@Component({
  selector: 'app-lang-switcher',
  standalone: true,
  imports: [IconComponent, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './lang-switcher.component.html',
  styleUrl: './lang-switcher.component.scss',
})
export class LangSwitcherComponent {
  readonly app = inject(AppService);
  private readonly host = inject(ElementRef<HTMLElement>);

  readonly open = signal<boolean>(false);
  readonly langs = LANGS;

  /** Flag emoji for the currently selected language — shown on the
   *  trigger button next to the code. */
  flagFor(code: Lang): string { return LANG_FLAGS[code] ?? ''; }

  /** Close the menu when the click lands outside this component. The
   *  document listener fires on {@code mousedown} so the dropdown
   *  collapses before a downstream {@code click} handler runs. */
  @HostListener('document:mousedown', ['$event'])
  onDocumentDown(ev: MouseEvent): void {
    if (!this.open()) return;
    if (!this.host.nativeElement.contains(ev.target as Node)) {
      this.open.set(false);
    }
  }

  /** Esc closes the menu without selecting anything. */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open()) this.open.set(false);
  }

  toggle(): void { this.open.update((o) => !o); }
  close(): void  { this.open.set(false); }

  pick(code: Lang): void {
    this.app.setLang(code);
    this.close();
  }
}
